import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  exportAdminSurveyResponses,
  getAdminSurveyResponse,
  listAdminSurveyResponses,
  listAdminSurveys,
} from "../../api/api";
import { Button } from "../ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import SurveyRunner from "../survey/SurveyRunner";
import { formatSubmittedAt } from "../survey/surveyUtils";
import {
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationTabHeader,
} from "../notifications_page/NotificationTabShared";

/**
 * Platform admin: survey catalog, submitted responses, read-only detail, JSON export.
 *
 * @param {Object} props
 * @param {number} props.adminId
 * @param {"authenticated"|"public"} [props.audience]
 * @param {string} [props.basePath]
 * @param {string} [props.listTitle]
 * @param {string} [props.listDescription]
 * @returns {JSX.Element}
 */
export default function AdminSurveysTab({
  adminId,
  audience = "authenticated",
  basePath = "/platform-admin/surveys",
  listTitle,
  listDescription,
}) {
  const { surveyId, responseId } = useParams();
  const parsedResponseId = responseId ? Number(responseId) : 0;
  const isPublic = audience === "public";
  const title = listTitle || (isPublic ? "Public feedback" : "Surveys");
  const description =
    listDescription ||
    (isPublic
      ? "Messages sent from the public feedback form. Click a survey to see responses."
      : "Review submitted beta feedback. Click a survey to see responses.");

  if (surveyId && parsedResponseId) {
    return (
      <AdminResponseDetail
        adminId={adminId}
        surveyId={surveyId}
        responseId={parsedResponseId}
        basePath={basePath}
      />
    );
  }
  if (surveyId) {
    return (
      <AdminResponseList
        adminId={adminId}
        surveyId={surveyId}
        basePath={basePath}
        isPublic={isPublic}
      />
    );
  }
  return (
    <AdminSurveyList
      adminId={adminId}
      audience={audience}
      basePath={basePath}
      title={title}
      description={description}
    />
  );
}

/**
 * @param {Object} props
 * @param {number} props.adminId
 * @param {"authenticated"|"public"} props.audience
 * @param {string} props.basePath
 * @param {string} props.title
 * @param {string} props.description
 * @returns {JSX.Element}
 */
function AdminSurveyList({ adminId, audience, basePath, title, description }) {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError("");
      const result = await listAdminSurveys(adminId, audience);
      if (!mounted) return;
      if (result.error) {
        setError(result.error);
      } else {
        setSurveys(result.data || []);
      }
      setLoading(false);
    }
    if (adminId) load();
    return () => {
      mounted = false;
    };
  }, [adminId, audience]);

  return (
    <div>
      <NotificationTabHeader title={title} description={description} />
      {error ? <NotificationErrorPanel message={error} /> : null}
      {loading ? (
        <NotificationEmptyState title="Loading surveys..." className="border-solid bg-ui-card" />
      ) : null}
      {!loading && surveys.length === 0 ? (
        <NotificationEmptyState title="No surveys" subtitle="Survey definition files have not been loaded." />
      ) : null}
      <ul className="flex flex-col gap-3">
        {surveys.map((survey) => (
          <li key={survey.id}>
            <Link
              to={`${basePath}/${survey.id}`}
              className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring"
            >
              <Card className="border-ui-border transition-colors hover:bg-ui-muted/40">
                <CardHeader className="py-4">
                  <CardTitle className="text-base">{survey.title}</CardTitle>
                  <CardDescription>
                    {survey.submitted_count} submitted
                    {survey.description ? ` · ${survey.description}` : ""}
                  </CardDescription>
                </CardHeader>
              </Card>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * @param {Object} props
 * @param {number} props.adminId
 * @param {string} props.surveyId
 * @param {string} props.basePath
 * @param {boolean} props.isPublic
 * @returns {JSX.Element}
 */
function AdminResponseList({ adminId, surveyId, basePath, isPublic }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError("");
      const result = await listAdminSurveyResponses(adminId, surveyId);
      if (!mounted) return;
      if (result.error) {
        setError(result.error);
      } else {
        setRows(result.data || []);
      }
      setLoading(false);
    }
    if (adminId && surveyId) load();
    return () => {
      mounted = false;
    };
  }, [adminId, surveyId]);

  /**
   * Download submitted responses as a JSON file.
   * @returns {Promise<void>}
   */
  async function onExport() {
    setExporting(true);
    const result = await exportAdminSurveyResponses(adminId, surveyId);
    setExporting(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    const blob = new Blob([JSON.stringify(result.data, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${surveyId}-responses.json`;
    anchor.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <Link to={basePath} className="text-sm text-accent hover:text-accent-hover">
          {isPublic ? "← Public feedback" : "← All surveys"}
        </Link>
        <Button type="button" variant="outline" size="sm" onClick={onExport} disabled={exporting}>
          Download JSON
        </Button>
      </div>
      <NotificationTabHeader
        title={surveyId}
        description="Submitted responses only. Click a row to read answers."
      />
      {error ? <NotificationErrorPanel message={error} /> : null}
      {loading ? (
        <NotificationEmptyState title="Loading responses..." className="border-solid bg-ui-card" />
      ) : null}
      {!loading && rows.length === 0 ? (
        <NotificationEmptyState title="No submissions yet" subtitle="Submitted responses will appear here." />
      ) : null}
      {!loading && rows.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-text-primary">
            <thead>
              <tr className="border-b border-ui-border text-text-muted">
                <th className="py-2 pr-3 font-medium">{isPublic ? "Name" : "User"}</th>
                <th className="py-2 pr-3 font-medium">Email</th>
                <th className="py-2 pr-3 font-medium">Submitted</th>
                <th className="py-2 font-medium">Version</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="cursor-pointer border-b border-ui-border hover:bg-ui-muted/40"
                  onClick={() => navigate(`${basePath}/${surveyId}/${row.id}`)}
                >
                  <td className="py-2.5 pr-3">
                    {row.username ? (
                      <>
                        {row.first_name} {row.last_name}{" "}
                        <span className="text-text-muted">@{row.username}</span>
                      </>
                    ) : (
                      row.first_name || "Anonymous"
                    )}
                  </td>
                  <td className="py-2.5 pr-3">{row.email}</td>
                  <td className="py-2.5 pr-3">{formatSubmittedAt(row.submitted_at)}</td>
                  <td className="py-2.5">{row.survey_version}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {Object} props
 * @param {number} props.adminId
 * @param {string} props.surveyId
 * @param {number} props.responseId
 * @param {string} props.basePath
 * @returns {JSX.Element}
 */
function AdminResponseDetail({ adminId, surveyId, responseId, basePath }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      setError("");
      const result = await getAdminSurveyResponse(adminId, surveyId, responseId);
      if (!mounted) return;
      if (result.error) {
        setError(result.error);
      } else {
        setDetail(result.data);
      }
      setLoading(false);
    }
    if (adminId && surveyId && responseId) load();
    return () => {
      mounted = false;
    };
  }, [adminId, surveyId, responseId]);

  const user = detail?.user;
  const response = detail?.response;
  const definition = detail?.definition;
  const identityLabel = user
    ? user.username
      ? `${user.first_name} ${user.last_name} (@${user.username}) · ${user.email}`
      : `${user.first_name || "Anonymous"}${user.email ? ` · ${user.email}` : ""}`
    : "Submitted response";

  return (
    <div>
      <Link
        to={`${basePath}/${surveyId}`}
        className="mb-4 inline-block text-sm text-accent hover:text-accent-hover"
      >
        ← Responses
      </Link>
      <NotificationTabHeader
        title={definition?.title || surveyId}
        description={identityLabel}
      />
      {response?.submitted_at ? (
        <p className="mb-4 text-sm text-text-muted">
          Submitted {formatSubmittedAt(response.submitted_at)} · version {response.survey_version}
        </p>
      ) : null}
      {error ? <NotificationErrorPanel message={error} /> : null}
      {loading ? (
        <NotificationEmptyState title="Loading response..." className="border-solid bg-ui-card" />
      ) : null}
      {definition && response ? (
        <CardContent className="p-0">
          <SurveyRunner
            definition={definition}
            initialAnswers={response.answers || {}}
            initialPageId={response.current_page_id || ""}
            mode="readonly"
          />
        </CardContent>
      ) : null}
    </div>
  );
}
